/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 *
 * Phase 6: Frontend Polish
 *
 * Features:
 * - Catches component crashes
 * - Shows user-friendly error message
 * - Provides retry functionality
 * - Logs errors for debugging
 * - Prevents entire app from crashing
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to external error tracking service (e.g., Sentry)
    // logErrorToService(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state if resetKeys change
    if (this.state.hasError && this.props.resetKeys) {
      const resetKeysChanged = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );

      if (resetKeysChanged) {
        this.resetError();
      }
    }
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleRetry = (): void => {
    this.resetError();
    // Force re-render by updating a key prop
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/admin';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary-container">
          <div className="error-boundary-content">
            {/* Icon */}
            <div className="error-boundary-icon">
              <AlertTriangle className="w-16 h-16 text-red-500" />
            </div>

            {/* Title */}
            <h1 className="error-boundary-title">Something went wrong</h1>

            {/* Description */}
            <p className="error-boundary-description">
              We're sorry, but something unexpected happened. The error has been logged
              and we'll look into it.
            </p>

            {/* Error Details (Development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="error-boundary-details">
                <h2 className="error-boundary-details-title">Error Details (Dev Only)</h2>
                <div className="error-boundary-error-message">
                  <strong>Error:</strong> {this.state.error.toString()}
                </div>
                {this.state.errorInfo && (
                  <div className="error-boundary-stack-trace">
                    <strong>Stack Trace:</strong>
                    <pre>{this.state.errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="error-boundary-actions">
              <button
                onClick={this.handleRetry}
                className="error-boundary-button error-boundary-button-primary"
              >
                <RefreshCw className="w-5 h-5" />
                <span>Reload Page</span>
              </button>

              <button
                onClick={this.handleGoHome}
                className="error-boundary-button error-boundary-button-secondary"
              >
                <Home className="w-5 h-5" />
                <span>Go to Home</span>
              </button>
            </div>

            {/* Help Text */}
            <p className="error-boundary-help">
              If this problem persists, please contact support with the error details above.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper for easier use with hooks
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.FC<P> => {
  return (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

// Styles (add to database.css)
export const errorBoundaryStyles = `
/* Error Boundary Container */
.error-boundary-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--color-bg-primary, #ffffff);
}

.error-boundary-content {
  max-width: 600px;
  width: 100%;
  text-align: center;
}

/* Icon */
.error-boundary-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

/* Title */
.error-boundary-title {
  font-size: 32px;
  font-weight: 700;
  color: var(--color-text-primary, #111827);
  margin-bottom: 16px;
}

/* Description */
.error-boundary-description {
  font-size: 16px;
  color: var(--color-text-secondary, #6b7280);
  line-height: 1.6;
  margin-bottom: 32px;
}

/* Error Details (Development) */
.error-boundary-details {
  background: var(--color-bg-secondary, #f9fafb);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 32px;
  text-align: left;
}

.error-boundary-details-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #111827);
  margin-bottom: 12px;
}

.error-boundary-error-message {
  font-size: 13px;
  color: var(--color-danger, #ef4444);
  margin-bottom: 12px;
  font-family: var(--font-mono, 'SF Mono', 'Monaco', 'Consolas', monospace);
  word-break: break-all;
}

.error-boundary-stack-trace {
  font-size: 12px;
  color: var(--color-text-secondary, #6b7280);
  font-family: var(--font-mono, 'SF Mono', 'Monaco', 'Consolas', monospace);
}

.error-boundary-stack-trace pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 8px;
  padding: 12px;
  background: white;
  border-radius: 4px;
  border: 1px solid var(--color-border, #e5e7eb);
}

/* Actions */
.error-boundary-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
}

.error-boundary-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.error-boundary-button-primary {
  background: var(--color-primary, #3b82f6);
  color: white;
}

.error-boundary-button-primary:hover {
  background: var(--color-primary-dark, #2563eb);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.error-boundary-button-secondary {
  background: var(--color-bg-secondary, #f3f4f6);
  color: var(--color-text-primary, #111827);
  border: 1px solid var(--color-border, #e5e7eb);
}

.error-boundary-button-secondary:hover {
  background: var(--color-bg-tertiary, #e5e7eb);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.error-boundary-button:active {
  transform: translateY(0);
}

/* Help Text */
.error-boundary-help {
  font-size: 14px;
  color: var(--color-text-secondary, #6b7280);
  margin: 0;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .error-boundary-title {
    font-size: 24px;
  }

  .error-boundary-description {
    font-size: 14px;
  }

  .error-boundary-actions {
    flex-direction: column;
  }

  .error-boundary-button {
    width: 100%;
    justify-content: center;
  }
}
`;

// Example usage in DatabasePage:
/*
import { ErrorBoundary } from './ErrorBoundary';

// Wrap entire page
<ErrorBoundary>
  <DatabasePage />
</ErrorBoundary>

// Wrap individual tabs
<ErrorBoundary
  onError={(error, errorInfo) => {
    console.log('Tab error:', error);
  }}
>
  <TablesTab />
</ErrorBoundary>

// With custom fallback
<ErrorBoundary fallback={<div>Custom error message</div>}>
  <PerformanceTab />
</ErrorBoundary>
*/
